import { Injectable } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';
import { ProjectSummaryRow } from '../../project-summary/data-access/project-summary.types';
import { ConfigService } from '../../config/config.service';
import { ManagePdfReportRequestLauncherService } from '../../reports/data-access/manage-pdf-report-request-launcher.service';

@Injectable()
export class LegacyNotificationActionsService {
    constructor(
        private upgrade: UpgradeModule,
        private cfg: ConfigService,
        private managePdfRequestLauncher: ManagePdfReportRequestLauncherService
    ) { }

    openProjectExportRequestDialog(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        this.seedLegacySelection(rows, selectedProjectIds);
        const service = this.getLegacyService<any>('exportRequestService');
        const openFromExportService = typeof service?.openProjectExportRequestDialog === 'function';

        if (openFromExportService && this.tryInvoke('exportRequestService.openProjectExportRequestDialog', () => service.openProjectExportRequestDialog())) {
            return;
        }

        if (!openFromExportService) {
            console.error('[notifications] Missing legacy method exportRequestService.openProjectExportRequestDialog');
        }

        this.openProjectExportRequestDialogFallback(rows, selectedProjectIds);
    }

    openReportRequestDialogSelectedProjects(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        if (this.cfg.managePdfReports().enable) {
            this.managePdfRequestLauncher.openForProjects(rows, selectedProjectIds);
            return;
        }

        this.seedLegacySelection(rows, selectedProjectIds);
        const service = this.getLegacyService<any>('reportRequestService');
        service?.openReportRequestDialog_selectedProjects?.();
    }

    openSelectedProjectsOnMap(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        this.seedLegacySelection(rows, selectedProjectIds);
        const service = this.getLegacyService<any>('geographicalProjectPresenterService');
        service?.post_selectedProjects?.();
    }

    private seedLegacySelection(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        const projectDataService = this.getLegacyService<any>('projectDataService');
        const trackingStatusService = this.getLegacyService<any>('trackingStatusService');
        const selected = new Set(selectedProjectIds);

        if (projectDataService) {
            projectDataService.projectNames = (rows || []).map((row) => ({
                projectID: row.projectID,
                trackingStatusID: this.toTrackingStatusId(row.trackingStatus),
                isChecked: selected.has(row.projectID)
            }));
        }

        if (trackingStatusService) {
            trackingStatusService.selectedValues = [1, 2, 3, 4, 5, 6];
        }
    }

    private openProjectExportRequestDialogFallback(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        const dialogService = this.getLegacyService<any>('dialogService');
        if (!dialogService?.openExportRequestDialog) {
            console.error('[notifications] Missing legacy method dialogService.openExportRequestDialog');
            return;
        }

        const selected = new Set(selectedProjectIds || []);
        let projectIds = (rows || [])
            .filter((row) => selected.has(row.projectID))
            .map((row) => row.projectID);

        if (!projectIds.length) {
            projectIds = (rows || [])
                .filter((row) => !row.excluded)
                .map((row) => row.projectID);
        }

        const exportParams = {
            exportFormat: 0,
            fileName: 'ProjectExport',
            projectIds,
            contracts: 'None',
            contractSelection: 0,
            includeContractors: true,
            includeEquipment: false
        };

        this.tryInvoke('dialogService.openExportRequestDialog', () => dialogService.openExportRequestDialog(exportParams));
    }

    private tryInvoke(actionName: string, action: () => unknown): boolean {
        try {
            action();
            return true;
        } catch (error) {
            console.error(`[notifications] Failed invoking ${actionName}`, error);
            return false;
        }
    }

    private toTrackingStatusId(status: string | undefined): number {
        switch ((status || '').toLowerCase()) {
            case 'active':
                return 1;
            case 'dormant':
                return 2;
            case 'future':
                return 3;
            case 'archived':
                return 4;
            case 'deleted':
                return 5;
            case 'cancelled':
                return 6;
            default:
                return 1;
        }
    }

    private getLegacyService<T>(name: string): T | null {
        const ng1Injector = this.getNg1Injector();
        if (!ng1Injector) {
            console.error(`[notifications] AngularJS injector unavailable while resolving ${name}`);
            return null;
        }

        try {
            return ng1Injector.get(name) as T;
        } catch {
            return null;
        }
    }

    private getNg1Injector(): any | null {
        const fromUpgrade = (this.upgrade as any)?.$injector;
        if (fromUpgrade) {
            return fromUpgrade;
        }

        const ng = (window as any)?.angular;
        if (!ng?.element) {
            return null;
        }

        try {
            return ng.element(document.body).injector() || ng.element(document.documentElement).injector() || null;
        } catch {
            return null;
        }
    }
}
